import md5 from "md5";
import { decode, encode, normalizeValue } from "../../vendor/universal-fingerprint-codec";

export const NOT_TRANSLATED = "Not Translated";

/**
 * Un token "clean" (key=value del codec) contiene sempre il separatore "=";
 * un token md5 legacy (32 cifre esadecimali) non può contenerlo mai.
 */
export const isCleanToken = (token) => typeof token === "string" && token.includes("=");

/**
 * Traduce i token di un fingerprint in Map<token, {key, value}>.
 *
 * - una voce per token DISTINTO, nell'ordine della prima occorrenza (i boost
 *   ripetono i token, come già faceva la traduzione via API);
 * - i token clean (key=value) sono decodificati in locale col codec;
 * - i restanti (md5) sono risolti da `lookupRemote(tokens)`, che restituisce
 *   l'array parallelo "key≠value" | null della tabella Redis; viene chiamata
 *   una sola volta e solo se ci sono token md5;
 * - fallback {key: token, value: "Not Translated"} per token malformati, non
 *   trovati o se la lookup remota fallisce (gli altri token restano tradotti).
 */
export async function translateTokens(tokens, lookupRemote) {
  const result = new Map();
  const remote = [];

  for (const token of tokens ?? []) {
    if (result.has(token)) continue;

    if (isCleanToken(token)) {
      try {
        const [key, value] = decode(token);
        result.set(token, { key, value });
      } catch (e) {
        result.set(token, { key: token, value: NOT_TRANSLATED });
      }
    } else {
      result.set(token, { key: token, value: NOT_TRANSLATED });
      remote.push(token);
    }
  }

  if (remote.length > 0) {
    let data = null;
    try {
      data = await lookupRemote(remote);
    } catch (e) {
      data = null;
    }

    if (Array.isArray(data)) {
      remote.forEach((token, index) => {
        const found = data[index];
        if (typeof found === "string" && found) {
          const [key, value] = found.split("≠");
          result.set(token, { key, value });
        }
      });
    }
  }

  return result;
}

/** Divide "key=value" al PRIMO "=": il valore puo' contenerne altri. */
export function splitPair(pair) {
  const text = String(pair ?? "");
  const idx = text.indexOf("=");
  return idx < 0 ? [text, ""] : [text.slice(0, idx), text.slice(idx + 1)];
}

/**
 * Termini `unlike` (more_like_this) dalle coppie "key=value" scelte dall'editor.
 *
 * Per ogni coppia produce sia il token md5 legacy sia il token clean del codec,
 * cosi' l'esclusione agisce qualunque sia il formato del fingerprint del
 * catalogo: un termine assente dall'indice e' innocuo. Il valore e' reso
 * Python-style con normalizeValue, come fa ares-pipeline. Le coppie senza
 * valore sono ignorate. Restituisce i token separati da spazio ("" se nessuno).
 */
export function unlikeTokens(pairs) {
  const tokens = [];
  for (const pair of pairs ?? []) {
    const [key, rawValue] = splitPair(pair);
    if (!rawValue) continue;
    const value = normalizeValue(rawValue);
    tokens.push(md5(`${key}${value}`), encode(key, value));
  }
  return tokens.join(" ");
}
